import { useState } from "react";
import { api } from "../../api/client";
import type { AuditLog, Page } from "../../api/types";
import { EmptyState, ErrorState, Loading, PageHead, Pagination, SelectField } from "../../components/ui";
import { formatDateTime } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { t } from "../../i18n";

const ACTION_LABEL: Record<string, string> = {
  "organization.create": t("Assessoria criada"),
  "organization.update": t("Assessoria alterada"),
  "coach.create": t("Treinador cadastrado"),
  "coach.update": t("Treinador alterado"),
  "group.create": t("Grupo criado"),
  "group.update": t("Grupo alterado"),
  "athlete.create": t("Atleta cadastrado"),
  "athlete.update": t("Cadastro de atleta alterado"),
  "athlete.profile_update": t("Perfil esportivo alterado"),
  "athlete.self_update": t("Atleta atualizou o próprio perfil"),
  "athlete.invite": t("Convite enviado ao atleta"),
  "auth.login": t("Login"),
  "auth.login_failed": t("Tentativa de login recusada"),
  "auth.logout": t("Logout"),
  "auth.invitation_accepted": t("Convite aceito"),
  "auth.password_reset": t("Senha redefinida"),
  "auth.password_changed": t("Senha alterada"),
  "auth.email_verified": t("E-mail confirmado"),
  "auth.refresh_reuse_detected": t("Sessão revogada por reutilização de token"),
  "admin.create": t("Administrador criado"),
  "demo.seed": t("Dados de demonstração criados"),
};

const FILTERS = [
  { value: "", label: t("Todas as ações") },
  { value: "athlete", label: t("Atletas") },
  { value: "coach", label: t("Treinadores") },
  { value: "group", label: t("Grupos") },
  { value: "organization", label: t("Assessorias") },
  { value: "auth", label: t("Acesso e senhas") },
];

const FIELD_LABEL: Record<string, string> = {
  full_name: t("nome"), name: t("nome"), email: t("e-mail"), phone: t("telefone"), birth_date: t("nascimento"),
  joined_at: t("ingresso"), coach_id: t("treinador"), group_id: t("grupo"), status: t("situação"), is_active: t("ativo"),
  send_invitation: t("convite"), description: t("descrição"), bio: t("apresentação"), slug: t("identificador"),
  plan: t("plano"), manager_email: t("e-mail do gestor"), running_experience: t("experiência"), running_since: t("corre desde"),
  training_history: t("histórico"), usual_weekly_volume_m: t("volume semanal"), best_marks: t("melhores marcas"),
  goals_text: t("objetivos"), preferred_days: t("dias preferidos"), preferred_time: t("horário"), equipment: t("equipamentos"),
  hr_max_bpm: t("FC máxima"), hr_rest_bpm: t("FC de repouso"), hr_low_max_bpm: t("limite da intensidade baixa"), hr_moderate_max_bpm: t("limite da intensidade moderada"), training_zones: t("zonas"), sport_notes: t("observações"),
};

function summarize(changes: Record<string, unknown>): string {
  const keys = Object.keys(changes)
    .filter((k) => k !== "note")
    .map((k) => FIELD_LABEL[k] ?? k);
  if (!keys.length) return "";
  return keys.slice(0, 4).join(", ") + (keys.length > 4 ? t(" e mais {n}", { n: keys.length - 4 }) : "");
}

export function AuditPage({ orgId }: { orgId?: string }) {
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const path = orgId ? `/orgs/${orgId}/audit-logs` : "/admin/audit-logs";
  const list = useAsync(() => api.get<Page<AuditLog>>(path, { action, page, page_size: 50 }), [path, action, page]);

  return (
    <>
      <PageHead
        title={orgId ? t("Auditoria") : t("Auditoria global")}
        subtitle={t("Registro de operações críticas: quem fez, o quê, quando e de onde. Senhas e tokens nunca são registrados.")}
      />
      <div className="toolbar">
        <SelectField label={t("Tipo de ação")} value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}>
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
      </div>
      {list.loading && !list.data && <Loading />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}
      {list.data && list.data.total === 0 && (
        <div className="panel">
          <EmptyState title={t("Nenhum registro para este filtro")} />
        </div>
      )}
      {list.data && list.data.total > 0 && (
        <>
          <div className="table-wrap">
            <table className="data responsive">
              <thead>
                <tr>
                  <th>{t("Quando")}</th>
                  <th>{t("Ação")}</th>
                  <th>{t("Quem")}</th>
                  <th>{t("Campos")}</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((l) => (
                  <tr key={l.id}>
                    <td className="num" style={{ whiteSpace: "nowrap" }}>{formatDateTime(l.created_at)}</td>
                    <td data-label={t("Ação")}>{ACTION_LABEL[l.action] ?? l.action}</td>
                    <td data-label={t("Quem")}>{l.actor_name ?? <span className="muted">{t("Sistema")}</span>}</td>
                    <td data-label={t("Campos")} className="muted">{summarize(l.changes) || "—"}</td>
                    <td data-label="IP" className="muted">{l.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={50} total={list.data.total} onPage={setPage} />
        </>
      )}
    </>
  );
}
